import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Context } from "../../../../../../store/store";
import { StoreContextType } from "@/store/types";
import { useTopicsWithScore } from "../../../../FuturebilityCockpit/hooks";
import TabSwitcher from "../../../components/tab-switcher";
import TextTabs from "../../../components/text-tabs";
import TopicScore from "../../../components/topic-score";
import { Button } from "../../../components";
import { useIsMobile } from "../../../components/audio-recorder/hooks/useIsMobile";
import { axiosPost } from "../../../../../../utils/queries";
import {
  DEFAULT_MAIN_TAB,
  DEFAULT_TEXT_TAB,
  HEIGHTS,
  MainTabIds,
  TextTabIds,
} from "./constants";
import { useUrlState } from "../../../hooks";
import { buildReflectionTestSetItem, getFilteredTopics } from "./utils";
import { RequestSchema } from "./types";
import { END_SUB_URL } from "../../../../constants";
import { Module } from "../../../../../../constants";
import { ReflectionType } from "../../../../../../types/domain-types";
import { API_URLS } from "../../../../../../utils/api-constants";
import round from "lodash-es/round";
import "../../../index.scss";

import "./styles.scss";

export interface AnalyticsPersonalSummaryProps {
  refetchTest: () => Promise<void>;
}

export function AnalyticsPersonalSummary({
  refetchTest,
}: AnalyticsPersonalSummaryProps) {
  const { t } = useTranslation();
  const { navigateToParticipationPage, sessionId } = useUrlState();
  const [{ publicTest }] = useContext<StoreContextType>(Context as any);
  const { _id } = publicTest;
  const isMobile = useIsMobile();
  const [activeMainTab, setActiveMainTab] =
    useState<MainTabIds>(DEFAULT_MAIN_TAB);
  const [activeTextTab, setActiveTextTab] =
    useState<TextTabIds>(DEFAULT_TEXT_TAB);
  const [isLoading, setIsLoading] = useState(false);

  const handleMainTabChange = (tabId: string) => {
    setActiveMainTab(tabId as MainTabIds);
    setActiveTextTab(DEFAULT_TEXT_TAB);
  };

  const handleTextTabChange = (tabId: string) => {
    setActiveTextTab(tabId as TextTabIds);
  };

  const handleNextClick = async () => {
    // Leverages and challenges are only reflected in the Reflection module
    if (!publicTest.client?.modules?.includes(Module.Reflection)) {
      navigateToParticipationPage({ subPage: END_SUB_URL });
      return;
    }

    setIsLoading(true);

    const items = [
      ...buildReflectionTestSetItem(challenges, ReflectionType.Challenge),
      ...buildReflectionTestSetItem(leverages, ReflectionType.Leverage),
    ];

    const requestData: RequestSchema = {
      testId: _id,
      sessionId,
      module: Module.Reflection,
      items,
    };

    try {
      await axiosPost(API_URLS.PUBLIC_TEST_SET_DYNAMIC_TEST_SETS, {
        data: requestData,
      });
      await refetchTest();
      navigateToParticipationPage({ subPage: END_SUB_URL });
    } catch (e: any) {
      toast.error(
        e?.response
          ? t("frontend.futurebilityCockpit.unknownError", "Unknown error")
          : t(
              "frontend.networkError",
              "Error: Blocked by Firewall or Internet connection error. Please contact your IT administrator"
            )
      );
      setIsLoading(false);
    }
  };

  const topics = useTopicsWithScore({
    currentTestId: _id,
    selectedFilters: undefined,
    rawData: {
      testSets: publicTest.sets,
      allEntries: publicTest.entries,
      tests: [publicTest],
      client: publicTest.client,
      dynamicTestSets: [],
    },
  });

  const { leverages, challenges, all } = getFilteredTopics(
    topics,
    activeTextTab
  );

  const filteredTopics = { leverages, challenges, all }[activeMainTab];

  return (
    <div className="tg_analytics-personal-summary">
      <div className="tg_column">
        <div className="header">
          <TabSwitcher
            activeTab={activeMainTab}
            onChange={handleMainTabChange}
          />
          {activeMainTab === DEFAULT_MAIN_TAB && (
            <TextTabs
              activeTextTab={activeTextTab}
              onChange={handleTextTabChange}
            />
          )}
        </div>
        <div
          className="score-wrapper"
          style={{
            height: isMobile
              ? HEIGHTS[activeMainTab].mobile
              : HEIGHTS[activeMainTab].desktop,
          }}
        >
          {filteredTopics.map((topic, i) => (
            <div className="topic-score-wrapper">
              <TopicScore key={i} topic={topic} />
              <div className="topic-circle">
                <span>{round(topic.scoreVsBenchmarkDifference)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="footer">
        <div className="tg_column">
          <Button
            next
            className="button"
            onClick={handleNextClick}
            disabled={isLoading}
          >
            {t("frontend.test.analyticsResults.next", "Next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
